import { useCallback, useMemo, useState } from "react";
import { useAccount } from "wagmi";

import { DEFAULT_VENUE, type VenueId } from "@/config/contracts";
import { CADC, USDC } from "@/config/tokens";
import { projectLoop, type LoopProjection } from "@/lib/loop/math";
import {
  accruedBorrowAssets,
  accruedTotalBorrowAssets,
} from "@/lib/loop/morpho";
import {
  computeAjnaRisk,
  computeRisk,
  type RiskMetrics,
} from "@/lib/loop/risk";
import { toRaw } from "@/lib/units";

import { useAjnaBorrower } from "./use-ajna-borrower";
import { useAjnaPool } from "./use-ajna-pool";
import { useMorphoMarket } from "./use-morpho-market";
import { useMorphoPosition } from "./use-morpho-position";

export type LoopFormState = {
  readonly principalText: string;
  readonly principal: number;
  readonly leverage: number;
  readonly slippageBps: number;
  readonly venue: VenueId;
  /** Ajna only: per-step borrow fraction in bps. Frontend-chosen; passed to loopIterative. */
  readonly ajnaStepFactorBps: number;
  readonly setPrincipalText: (s: string) => void;
  readonly setLeverage: (n: number) => void;
  readonly setSlippageBps: (n: number) => void;
  readonly setVenue: (v: VenueId) => void;
  readonly setAjnaStepFactorBps: (bps: number) => void;
  readonly projection: LoopProjection;
  /** Max selectable leverage for the active venue (on-chain MAX_LEVERAGE_BPS). */
  readonly maxLeverage: number;
  readonly isValid: boolean;
  readonly principalError: string | null;
  /** Borrow APR as decimal. null if not loaded. */
  readonly borrowApr: number | null;
  /** Available USDC liquidity on the market, human units. */
  readonly availableLiquidity: number | null;
  /** Risk for existing position only. */
  readonly currentRisk: RiskMetrics | null;
  /** Risk after the loop is applied on top of existing position. */
  readonly projectedRisk: RiskMetrics | null;
  /** Existing collateral (CADC raw, 1e18). null if data not loaded / wrong venue. */
  readonly existingCollateralRaw: bigint | null;
  /** Existing borrow assets (USDC raw, 1e6). null if data not loaded / wrong venue. */
  readonly existingBorrowRaw: bigint | null;
};

// Morpho's LLTV for the CADC/USDC market on Base. Also the contract's
// STEP_FACTOR (per-step borrow factor = LLTV directly). Passed to projectLoop
// for the liquidation-buffer estimate. Real risk data comes from computeRisk
// via on-chain oracle + position.
const LTV = 0.86;
const DEFAULT_LEVERAGE = 2.5;
const DEFAULT_SLIPPAGE_BPS = 30;
const DEFAULT_AJNA_STEP_FACTOR_BPS = 7_000;

// Morpho's max leverage mirrors its on-chain MAX_LEVERAGE_BPS (LLTV 86%).
// Geometric ceiling = 1/(1−0.86) ≈ 7.14×; 99% headroom → 70714 bps = 7.07×.
const MORPHO_MAX_LEVERAGE = 7.07;

// Mirrors LoopMath.maxReachableLeverageBps in integer arithmetic.
// Returns max leverage as a decimal (e.g. 3.29 for stepFactorBps=7000).
const ajnaMaxLeverage = (stepFactorBps: number): number => {
  const ceiling = Math.floor((10_000 * 10_000) / (10_000 - stepFactorBps));
  const bps = Math.floor((ceiling * 9_900) / 10_000);
  return Math.floor(bps / 100) / 100;
};

const parsePrincipal = (text: string): number => {
  const cleaned = text.replace(/[^0-9.]/g, "");
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const useLoopForm = (): LoopFormState => {
  const [principalText, setPrincipalText] = useState("");
  const [leverage, setLeverage] = useState(DEFAULT_LEVERAGE);
  const [slippageBps, setSlippageBps] = useState(DEFAULT_SLIPPAGE_BPS);
  const [venue, setVenueRaw] = useState<VenueId>(DEFAULT_VENUE);
  const [ajnaStepFactorBps, setAjnaStepFactorBpsRaw] = useState(DEFAULT_AJNA_STEP_FACTOR_BPS);

  // Switching venues clamps leverage to the new venue's cap.
  const setVenue = useCallback((v: VenueId) => {
    setVenueRaw(v);
    const cap = v === "morpho" ? MORPHO_MAX_LEVERAGE : ajnaMaxLeverage(ajnaStepFactorBps);
    setLeverage((lev) => Math.min(lev, cap));
  }, [ajnaStepFactorBps]);

  // Changing step factor updates the Ajna leverage ceiling and clamps the slider.
  const setAjnaStepFactorBps = useCallback((bps: number) => {
    setAjnaStepFactorBpsRaw(bps);
    const cap = ajnaMaxLeverage(bps);
    setLeverage((lev) => Math.min(lev, cap));
  }, []);

  const principal = parsePrincipal(principalText);

  const { address: account } = useAccount();

  const isMorpho = venue === "morpho";
  const marketQuery = useMorphoMarket();
  const market = isMorpho ? marketQuery.data ?? null : null;
  const positionQuery = useMorphoPosition(isMorpho ? account : undefined);
  const position = isMorpho ? positionQuery.data ?? null : null;

  // Ajna parity: pool + borrower reads only fire when venue=ajna so we don't
  // burn RPC quota on a venue the user isn't looking at.
  const ajnaPoolQuery = useAjnaPool();
  const ajnaPool = !isMorpho ? ajnaPoolQuery.data ?? null : null;
  const ajnaBorrowerQuery = useAjnaBorrower(!isMorpho ? account : undefined);
  const ajnaBorrower = !isMorpho ? ajnaBorrowerQuery.data ?? null : null;

  const morphoAvailable = useMemo(() => {
    if (!market) return null;
    return Number(market.availableLiquidity) / 10 ** USDC.decimals;
  }, [market]);

  // Ajna stores quote-token (USDC) in 1e18 WAD scale internally — divide by
  // 1e12 to get USDC-native (1e6) before reporting human units.
  const ajnaAvailable = useMemo(() => {
    if (!ajnaPool) return null;
    const native = ajnaPool.availableLiquidity / 10n ** 12n;
    return Number(native) / 10 ** USDC.decimals;
  }, [ajnaPool]);

  const availableLiquidity = isMorpho ? morphoAvailable : ajnaAvailable;

  // USDC per 1 CADC, from the active venue's oracle. 0 until data loads — by
  // which point the user typically hasn't entered a principal yet.
  const collateralPriceUsdc = useMemo(() => {
    if (isMorpho) {
      if (!market) return 0;
      // Morpho oracle is scaled by 10^(36 + loanDec − collateralDec).
      const expo = 36 + USDC.decimals - CADC.decimals;
      return Number(market.oraclePrice) / 10 ** expo;
    }
    // Ajna lup is a WAD (1e18) quote-per-collateral price.
    if (!ajnaPool) return 0;
    return Number(ajnaPool.lup) / 1e18;
  }, [isMorpho, market, ajnaPool]);

  const projection = useMemo(
    () =>
      projectLoop({
        principal,
        targetLeverage: leverage,
        ltv: LTV,
        slippageBps,
        collateralPriceUsdc,
      }),
    [principal, leverage, slippageBps, collateralPriceUsdc],
  );

  // Borrow shares → assets, with Morpho interest accrued off-chain from the
  // on-chain `lastUpdate` anchor to the market fetch time (`dataUpdatedAt`).
  // Sub-refetch drift is immaterial for a risk readout.
  const nowSec = BigInt(Math.floor(marketQuery.dataUpdatedAt / 1000));
  const existingBorrowAssets = useMemo<bigint>(
    () =>
      market && position
        ? accruedBorrowAssets(
            position.borrowShares,
            market.state.totalBorrowShares,
            accruedTotalBorrowAssets(
              market.state,
              market.borrowRatePerSec,
              nowSec,
            ),
          )
        : 0n,
    [market, position, nowSec],
  );

  // Ajna debt: currentDebtWad = t0debt × inflator / 1e18. Convert to USDC
  // native (1e6) by dividing by 1e12.
  const ajnaCurrentDebtRaw = useMemo<bigint>(() => {
    if (!ajnaPool || !ajnaBorrower) return 0n;
    if (ajnaBorrower.t0debt === 0n) return 0n;
    const wad = (ajnaBorrower.t0debt * ajnaPool.inflator) / 10n ** 18n;
    return wad / 10n ** 12n;
  }, [ajnaPool, ajnaBorrower]);

  const currentRisk = useMemo<RiskMetrics | null>(() => {
    if (isMorpho) {
      if (!market || !position) return null;
      return computeRisk({
        collateralRaw: position.collateral,
        borrowRaw: existingBorrowAssets,
        oraclePrice: market.oraclePrice,
        lltv: market.params.lltv,
        loanDecimals: USDC.decimals,
        collateralDecimals: CADC.decimals,
      });
    }
    if (!ajnaPool || !ajnaBorrower) return null;
    return computeAjnaRisk({
      collateralRaw: ajnaBorrower.collateral,
      debtUsdcRaw: ajnaCurrentDebtRaw,
      lup: ajnaPool.lup,
    });
  }, [
    isMorpho,
    market,
    position,
    existingBorrowAssets,
    ajnaPool,
    ajnaBorrower,
    ajnaCurrentDebtRaw,
  ]);

  const projectedRisk = useMemo<RiskMetrics | null>(() => {
    // `projection.totalBorrow` is already USDC (priced via collateralPriceUsdc),
    // so it maps straight to loan-asset raw for both venues.
    const addedCollateralRaw = toRaw(projection.totalCollateral, CADC.decimals);
    const addedBorrowRaw = toRaw(projection.totalBorrow, USDC.decimals);
    if (isMorpho) {
      if (!market) return null;
      const baseCollateral = position?.collateral ?? 0n;
      const baseBorrow = existingBorrowAssets;
      return computeRisk({
        collateralRaw: baseCollateral + addedCollateralRaw,
        borrowRaw: baseBorrow + addedBorrowRaw,
        oraclePrice: market.oraclePrice,
        lltv: market.params.lltv,
        loanDecimals: USDC.decimals,
        collateralDecimals: CADC.decimals,
      });
    }
    if (!ajnaPool) return null;
    const baseCollateral = ajnaBorrower?.collateral ?? 0n;
    const baseBorrow = ajnaBorrower ? ajnaCurrentDebtRaw : 0n;
    return computeAjnaRisk({
      collateralRaw: baseCollateral + addedCollateralRaw,
      debtUsdcRaw: baseBorrow + addedBorrowRaw,
      lup: ajnaPool.lup,
    });
  }, [
    isMorpho,
    market,
    position,
    existingBorrowAssets,
    ajnaPool,
    ajnaBorrower,
    ajnaCurrentDebtRaw,
    projection,
  ]);

  const principalError = useMemo<string | null>(() => {
    if (principal <= 0) return null;
    if (availableLiquidity == null) return null;
    if (projection.totalBorrow > availableLiquidity) {
      const fmt = availableLiquidity.toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });
      return `Required borrow exceeds available liquidity (${fmt} USDC).`;
    }
    return null;
  }, [principal, availableLiquidity, projection.totalBorrow]);

  const maxLeverage = isMorpho ? MORPHO_MAX_LEVERAGE : ajnaMaxLeverage(ajnaStepFactorBps);

  const isValid =
    principal > 0 &&
    leverage >= 1 &&
    leverage <= maxLeverage &&
    principalError == null;

  return {
    principalText,
    principal,
    leverage,
    slippageBps,
    venue,
    ajnaStepFactorBps,
    setPrincipalText,
    setLeverage,
    setSlippageBps,
    setVenue,
    setAjnaStepFactorBps,
    projection,
    maxLeverage,
    isValid,
    principalError,
    borrowApr: isMorpho
      ? market?.borrowApr ?? null
      : ajnaPool?.borrowApr ?? null,
    availableLiquidity,
    currentRisk,
    projectedRisk,
    existingCollateralRaw: isMorpho
      ? position?.collateral ?? null
      : ajnaBorrower?.collateral ?? null,
    existingBorrowRaw: isMorpho
      ? position
        ? existingBorrowAssets
        : null
      : ajnaBorrower
        ? ajnaCurrentDebtRaw
        : null,
  };
};
